import { Button } from "../../shared";
import {
    Box,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Divider,
    Skeleton,
} from "@mui/joy";
import { MessageSquareText, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AuthenticatedImage } from "..";
import { ImageWithMetadata } from "../../constants/inspectionPhotos";
import { isBlobImageUrl } from "../../utils/taskAssetImages";

interface ImagePreviewModalProps {
    image: ImageWithMetadata | null;
    onClose: () => void;
    onDelete?: () => void;
    isDeleting?: boolean;
}

export function ImagePreviewModal(props: ImagePreviewModalProps) {
    const { image, onClose, onDelete, isDeleting = false } = props;
    const assetId = image?.assetId || null;
    const previewUrl = isBlobImageUrl(image?.url) ? image?.url || null : null;
    const note = image?.note || null;
    const isOpen = !!(previewUrl || assetId);
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        setImageLoaded(false);
    }, [previewUrl, assetId, isOpen]);

    return (
        <Modal open={isOpen} onClose={onClose}>
            <ModalDialog
                layout="center"
                variant="soft"
                sx={{
                    maxWidth: "min(960px, 94vw)",
                    width: "100%",
                    p: 0,
                    gap: 0,
                    overflow: "hidden",
                    borderRadius: "4px",
                }}
            >
                <DialogTitle sx={{ px: 2, pt: 2, pb: 1.5, m: 0 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Typography level="title-md">Photo Preview</Typography>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={onClose}
                            sx={{ minHeight: "auto", p: 0.5 }}
                        >
                            <X size={18} />
                        </Button>
                    </Box>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 0, overflow: "hidden", gap: 0 }}>
                    {(previewUrl || assetId) && (
                        <Box
                            sx={{
                                position: "relative",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                minHeight: 280,
                                maxHeight: "min(72vh, 720px)",
                                overflow: "hidden",
                                bgcolor: "background.level2",
                            }}
                        >
                            {!imageLoaded && (
                                <Skeleton
                                    variant="rectangular"
                                    animation="wave"
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        width: "100%",
                                        height: "100%",
                                        minHeight: 280,
                                        borderRadius: 0,
                                    }}
                                />
                            )}
                            <Box
                                sx={{
                                    width: "100%",
                                    opacity: imageLoaded ? 1 : 0,
                                    transition: "opacity 0.25s ease",
                                }}
                            >
                                <AuthenticatedImage
                                    assetId={assetId}
                                    url={previewUrl}
                                    alt="Preview"
                                    objectFit="contain"
                                    width="100%"
                                    height="auto"
                                    suppressLoadingUI
                                    onLoad={() => setImageLoaded(true)}
                                    sx={{
                                        width: "100%",
                                        maxHeight: "min(72vh, 720px)",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    <Box
                        sx={{
                            width: "100%",
                            borderTop: "1px solid",
                            borderColor: "divider",
                            px: 2,
                            py: 1.5,
                            bgcolor: "background.surface",
                        }}
                    >
                        {note ? (
                            <>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.75}
                                    mb={0.75}
                                >
                                    <MessageSquareText size={14} />
                                    <Typography
                                        level="body-sm"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Note
                                    </Typography>
                                </Box>
                                <Typography
                                    level="body-sm"
                                    sx={{
                                        color: "text.secondary",
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {note}
                                </Typography>
                            </>
                        ) : (
                            <Typography
                                level="body-xs"
                                sx={{
                                    color: "text.tertiary",
                                    fontStyle: "italic",
                                }}
                            >
                                No note added for this photo.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ px: 2, py: 1.5, m: 0 }}>
                    {onDelete && (
                        <Button
                            color="danger"
                            variant="soft"
                            startDecorator={<Trash2 size={16} />}
                            onClick={onDelete}
                            loading={isDeleting}
                            sx={{ mr: "auto" }}
                        >
                            Delete
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="neutral"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    );
}
